import Image from 'next/image';

import { Box, Flex, Heading, List, ListItem, Text } from '@chakra-ui/react';
import { ArrowRightIcon, ChatIcon, CheckIcon } from '@udacity/chakra-uds-icons';

import { ButtonLink } from '~/components/button-link';
import { ContentContainer } from '~/components/container';
import { trackEvent } from '~/features/analytics/track-event';

const FEATURES = [
  'Hands-on project-based approach',
  'Top content co-developed with industry leaders',
  'Global mentor network with 700+ domain experts',
];

export function PlansBusinessBannerSection() {
  return (
    <Box as='section' bgColor='grey.50' pb={{ base: 0, md: 16, lg: 24 }}>
      <ContentContainer contentMaxWidth='content-max-width-md' mobilePadding='0px'>
        <Box
          bgColor='grey.900'
          borderRadius={{ base: '0', md: '8px' }}
          color='white'
          minH={{ md: '425px' }}
          overflow='hidden'
          position='relative'
        >
          <Box bottom={0} left={0} opacity={{ base: 0.2, md: 1 }} position='absolute' right={0} top={0} aria-hidden>
            <Image
              alt=''
              quality={90}
              sizes='(min-width: 1024px) 1056px, 100vw'
              src='/images/plans/udacity-for-business-bg.png'
              style={{
                objectFit: 'cover',
                objectPosition: 'right center',
              }}
              fill
            />
          </Box>
          <Box
            bgGradient='linear(90deg, rgba(10, 11, 15, 0.95) 0%, rgba(10, 11, 15, 0.8) 45%, rgba(10, 11, 15, 0) 80%)'
            inset={0}
            pointerEvents='none'
            position='absolute'
            aria-hidden
          />
          <Flex
            flexDirection='column'
            gap={{ base: 8, md: 10 }}
            maxW={{ lg: '640px' }}
            position='relative'
            px={{ base: 4, md: 10, lg: 14 }}
            py={{ base: 8, md: 10, lg: 14 }}
            zIndex={1}
          >
            <Box>
              <Heading
                as='h2'
                fontSize={{ base: '2.375rem', md: '2.75rem', lg: '3.375rem' }}
                fontWeight='600'
                letterSpacing='-0.02em'
                lineHeight='1.1'
                mb={2}
                whiteSpace={{ lg: 'nowrap' }}
              >
                Udacity for Business
              </Heading>
              <Text fontSize={{ base: 'md', md: 'lg' }} lineHeight={{ base: '1.4', md: '1.5' }}>
                Scale skills development for your organization
              </Text>
            </Box>
            <List display='flex' flexDirection='column' gap={{ base: 3, md: 4 }}>
              {FEATURES.map((feature) => (
                <ListItem key={feature} alignItems={{ base: 'flex-start', md: 'center' }} display='flex' gap={2.5}>
                  <CheckIcon color='blue.500' flexShrink={0} height='20px' mt={{ base: '1px', md: '0' }} width='20px' />
                  <Text fontSize={{ base: 'md', md: 'lg' }} lineHeight={{ base: '1.4', md: '1.5' }}>
                    {feature}
                  </Text>
                </ListItem>
              ))}
            </List>
            <Flex gap={2} maxW={{ md: '360px' }} width='100%'>
              <ButtonLink
                ariaLabel='Learn more about Udacity for Business'
                buttonProps={{
                  colorScheme: 'blue',
                  variant: 'solid',
                  size: 'icon-left',
                  leftIcon: <ArrowRightIcon height='18px' width='18px' />,
                  flex: '1',
                  minW: 0,
                  onClick: () =>
                    trackEvent({
                      name: 'Plans CTA Clicked',
                      type: 'click',
                      label: 'plans_business_learn_more_clicked',
                    }),
                }}
                linkProps={{
                  href: '/enterprise/overview',
                }}
              >
                Learn More
              </ButtonLink>
              <ButtonLink
                ariaLabel='Contact us about Udacity for Business'
                buttonProps={{
                  variant: 'outline',
                  size: 'icon-left',
                  leftIcon: <ChatIcon height='18px' width='18px' />,
                  border: 'none',
                  boxShadow: 'inset 0 0 0 1.5px white',
                  color: 'white',
                  flex: '1',
                  minW: 0,
                  onClick: () =>
                    trackEvent({
                      name: 'Plans CTA Clicked',
                      type: 'click',
                      label: 'plans_business_contact_clicked',
                    }),
                  _hover: {
                    bgColor: 'whiteAlpha.200',
                  },
                  _active: {
                    bgColor: 'whiteAlpha.300',
                  },
                }}
                linkProps={{
                  href: '/get-started/enterprise',
                }}
              >
                Contact Us
              </ButtonLink>
            </Flex>
          </Flex>
        </Box>
      </ContentContainer>
    </Box>
  );
}
